/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps } from 'react'
import { forwardRef } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { cn } from '../../../lib/utils'
import type { CalendarProps, DateTimePickerProps } from '../../ui/datetime-picker'
import { DateTimePicker } from '../../ui/datetime-picker'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'

function zeroSecondsAndMs(value: Date): Date {
  const d = new Date(value.getTime())
  d.setSeconds(0, 0)
  return d
}

function normalizeFieldDate(value: unknown): Date | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  let parsed: Date | undefined
  if (value instanceof Date) {
    parsed = Number.isNaN(value.getTime()) ? undefined : value
  } else if (typeof value === 'string') {
    const fromString = new Date(value)
    parsed = Number.isNaN(fromString.getTime()) ? undefined : fromString
  }
  return parsed === undefined ? undefined : zeroSecondsAndMs(parsed)
}

export type DateTimeFormFieldProps = {
  datetimePickerProps?: Omit<DateTimePickerProps & CalendarProps, 'value' | 'onChange'>
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  required?: boolean
  validation?: RegisterOptions
}

export const DateTimeFormField = forwardRef<HTMLDivElement, DateTimeFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1">
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <div className={cn('w-full')} ref={forwardedRef}>
            <DateTimePicker
              {...props.datetimePickerProps}
              disabled={props.disabled ?? props.datetimePickerProps?.disabled}
              displaySeconds={props.datetimePickerProps?.displaySeconds ?? false}
              onChange={(date) => {
                field.onChange(date === undefined ? undefined : zeroSecondsAndMs(date))
              }}
              timePicker={{
                hour: props.datetimePickerProps?.timePicker?.hour ?? true,
                minute: props.datetimePickerProps?.timePicker?.minute ?? true,
                second: false,
              }}
              use12HourFormat={props.datetimePickerProps?.use12HourFormat ?? true}
              value={normalizeFieldDate(field.value)}
            />
          </div>
        )}
        rules={props.validation}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

DateTimeFormField.displayName = 'DateTimeFormField'
